import React, { useId } from "react";

// One labelled form control. Owns the id so the label, hint and error are
// always wired to the control, which is what most hand-rolled forms here got
// wrong (WCAG 1.3.1, 3.3.1, 3.3.2, 4.1.2).
//
//   <Field label="Email" hint="We never share it" error={errors.email}>
//     <input type="email" className={inputCls} value={email} onChange={...} />
//   </Field>
//
// `children` must be the control itself, not a wrapper around it: the id and
// aria attributes go on the direct child. `labelHidden` keeps the label for
// screen readers on designs with no visible label. Lint trusts a control
// only when it is the direct child of a Field (see eslint.config.js).
export default function Field({
  label,
  hint,
  error,
  required = false,
  labelHidden = false,
  className,
  labelClassName = "block text-sm text-secondary mb-1",
  hintClassName = "mt-1 text-xs text-tertiary",
  errorClassName = "mt-1 text-xs text-red-700",
  children,
}) {
  const autoId = useId();
  const isElement = React.isValidElement(children);
  const controlId = isElement ? children.props.id || autoId : undefined;
  const hintId = hint ? `${controlId || autoId}-hint` : undefined;
  const errorId = error ? `${controlId || autoId}-error` : undefined;

  const control = isElement
    ? React.cloneElement(children, {
        id: controlId,
        "aria-describedby":
          [children.props["aria-describedby"], hintId, errorId]
            .filter(Boolean)
            .join(" ") || undefined,
        "aria-invalid": error ? true : children.props["aria-invalid"],
        "aria-required": required ? true : children.props["aria-required"],
      })
    : children;

  return (
    <div className={className}>
      <label
        htmlFor={controlId}
        className={labelHidden ? "sr-only" : labelClassName}
      >
        {label}
        {required && !labelHidden && (
          <span aria-hidden="true"> *</span>
        )}
      </label>
      {control}
      {hint && (
        <div id={hintId} className={hintClassName}>
          {hint}
        </div>
      )}
      {error && (
        <p id={errorId} role="alert" className={errorClassName}>
          {error}
        </p>
      )}
    </div>
  );
}
